import Vue from 'vue';
import VueGtag from 'vue-gtag';
import store from '@/store/store';

const CATEGORY = {
  ENGAGEMENT: 'engagement',
  HELP: 'help',
  UA: 'user_acquisition',
  EXTERNAL_LINK: 'external_link',
  UPSELL: 'upsell',
};

export const getIdentification = () => {
  const currentUser = store.getters['auth/currentUser'];
  const tenant = store.getters['tenant/tenant'];

  // No identification before authentication (e.g. events fired from the login page).
  if (!currentUser) {
    return {};
  }

  return {
    aloha_tenant_id: currentUser.tenantId,
    aloha_tenant_name: tenant && tenant.name,
    aloha_user_id: currentUser.id,
  };
};

export const install = (vueInstance, options, router) => {
  Vue.use(VueGtag, options, router);

  const trackEvent = (event, ...args) => {
    vueInstance.prototype.$gtag.event(event, Object.assign({}, ...args, getIdentification()));
  };

  const trackScreenview = (event, ...args) => {
    vueInstance.prototype.$gtag.screenview(event, Object.assign({}, ...args, getIdentification()));
  };

  vueInstance.prototype.$track = {
    // Events
    login() {
      trackEvent('login', { event_category: CATEGORY.ENGAGEMENT });
    },
    signOut() {
      trackEvent('sign_out', { event_category: CATEGORY.ENGAGEMENT });
    },
    contactUs(method) {
      trackEvent('contact_us', { event_category: CATEGORY.HELP, event_label: method });
    },
    openHelpCenter() {
      trackEvent('open_help_center', { event_category: CATEGORY.HELP });
    },
    searchFiltersSearch() {
      trackEvent('search_filters_search', { event_category: CATEGORY.ENGAGEMENT });
    },
    searchFilterCategory(method) {
      trackEvent('search_filter_category', { event_category: CATEGORY.ENGAGEMENT, event_label: method });
    },
    searchFilterGeoZone() {
      trackEvent('search_filter_geo_zone', { event_category: CATEGORY.ENGAGEMENT });
    },
    searchFilterKeywords() {
      trackEvent('search_filter_keywords', { event_category: CATEGORY.ENGAGEMENT });
    },
    tenderArchive() {
      trackEvent('tender_archive', {
        event_category: CATEGORY.ENGAGEMENT,
      });
    },
    tenderUnarchive() {
      trackEvent('tender_unarchive', { event_category: CATEGORY.ENGAGEMENT });
    },
    tenderFollow(method) {
      trackEvent('tender_follow', { event_category: CATEGORY.ENGAGEMENT, event_label: method });
    },
    tenderUnfollow(method) {
      trackEvent('tender_unfollow', { event_category: CATEGORY.ENGAGEMENT, event_label: method });
    },
    tenderShare(method) {
      trackEvent('tender_share', { event_category: CATEGORY.ENGAGEMENT, event_label: method });
    },
    tenderUnshare(method) {
      trackEvent('tender_unshare', { event_category: CATEGORY.ENGAGEMENT, event_label: method });
    },
    tenderCopyLink() {
      trackEvent('tender_copy_link', { event_category: CATEGORY.ENGAGEMENT });
    },
    tenderSourceClick() {
      trackEvent('tender_get_source', { event_category: CATEGORY.ENGAGEMENT });
    },
    tenderRead() {
      trackEvent('tender_read', { event_category: CATEGORY.ENGAGEMENT });
    },
    tenderUnread() {
      trackEvent('tender_unread', { event_category: CATEGORY.ENGAGEMENT });
    },
    tenderBuyDocument(source) {
      trackEvent('tender_buy_document', {
        event_category: CATEGORY.EXTERNAL_LINK,
        event_label: source.toLowerCase(),
      });
    },
    tenderExternalLink(source) {
      trackEvent('tender_external_link', {
        event_category: CATEGORY.EXTERNAL_LINK,
        event_label: source.toLowerCase(),
      });
    },
    tendersTablePageChange() {
      trackEvent('tenders_table_page_change', { event_category: CATEGORY.ENGAGEMENT });
    },
    inviteSubmit() {
      trackEvent('invite_submit', { event_category: CATEGORY.UA });
    },
    inviteSuccess() {
      trackEvent('invite_success', { event_category: CATEGORY.UA });
    },
    tenderChange() {
      trackEvent('tender_change', { event_category: CATEGORY.ENGAGEMENT });
    },
    keywordsExplanation() {
      trackEvent('keywords_explanation', { event_category: CATEGORY.HELP });
    },
    // Screenviews
    filterCategoryAndZoneDialog() {
      trackScreenview({ screen_name: 'filter_category_zone_dialog' });
    },
    filterKeywordsDialog() {
      trackScreenview({ screen_name: 'filter_keywords_dialog' });
    },
    tenderView() {
      trackEvent('tender_view', { event_category: CATEGORY.ENGAGEMENT });
      trackScreenview({ screen_name: 'tender_details_page' });
    },
    opportunitiesView() {
      trackEvent('opportunities_page_clicked', { event_category: CATEGORY.ENGAGEMENT });
      trackScreenview({ screen_name: 'opportunities_page' });
    },
    exploreView() {
      trackEvent('explore_page_clicked', { event_category: CATEGORY.ENGAGEMENT });
      trackScreenview({ screen_name: 'explore_page' });
    },
    // BI add-on upsell instrumentation
    biUpsellView() {
      trackEvent('bi_upsell_view', {
        event_category: CATEGORY.UPSELL,
      });
    },
    preProjectsUpsellView() {
      trackEvent('pre_projects_upsell_view', {
        event_category: CATEGORY.UPSELL,
      });
    },
    preProjectShare(method) {
      trackEvent('pre_project_share', { event_category: CATEGORY.ENGAGEMENT, event_label: method });
    },
    preProjectUnshare(method) {
      trackEvent('pre_project_unshare', { event_category: CATEGORY.ENGAGEMENT, event_label: method });
    },
  };
};

export const track = { install };
